// knowledgeDetails.pipeline.configuration.* — owned by the configuration page.
export default {
  heading: 'Configuration — {{name}}',
  legend: 'Badges:',
  strip: {
    label: 'Pipeline stages and their current settings',
    tokens: '{{count}} tokens',
    tenantModel: 'tenant chat model',
    enrich: 'keywords {{keywords}} · questions {{questions}}',
    pageRank: 'page rank {{value}}',
    jump: 'Go to the {{stage}} settings',
  },
  preview: {
    title: 'A real chunk, as stored now',
    intro: 'One chunk of this knowledge base as it is embedded today. Chunk',
    page: 'page {{page}}',
    keywords: 'Keywords',
    questions: 'Questions',
    none: 'none',
    docChunks: '{{count}} chunks in this document',
    loading: 'Loading a chunk…',
    empty:
      'No parsed document yet. Once a document is parsed, one of its chunks shows here.',
  },
  subtitle:
    'Every setting sits under the stage it changes. The badge says when it takes effect.',
  documents: 'documents',
  chunks: 'chunks',
  tokens: 'tokens',
  thinkingBudgetAddon: 'tokens (from default model settings)',
  general: {
    title: 'Knowledge base',
    hint: 'Name, description and who can manage it. These apply on Save.',
  },
  sections: {
    enrich: 'Enrich',
    enrichHint:
      'Optional LLM passes over each chunk before it is embedded: keywords, questions, tags and RAPTOR summaries.',
    noSettings: 'No settings — read only',
  },
  chunk: {
    methodResetNote:
      'Applies to documents added after Save; existing documents keep their own method (change it per document in Documents). Switching to General resets the chunk size to 128, and to Knowledge Graph to 8192.',
  },
  embed: {
    lockedReason:
      'Cannot change: {{count}} chunks are already embedded with this model, and nothing re-embeds them. Delete the documents or create a new knowledge base to switch.',
  },
  index: {
    hint: 'What is in the search index for this knowledge base right now. Re-parse recomputes it.',
  },
  retrieve: {
    testingLink: 'Try retrieval settings in Retrieval testing',
  },
  contextualise: {
    title: 'Contextualisation',
    llmTip: 'The LLM model used to contextualise chunks.',
    llmPlaceholder: 'Select LLM model for contextualising chunks',
    cacheTip: 'Whether to cache the document content for faster processing.',
    strategyTip:
      "The caching strategy. 'Auto' picks the best strategy for the selected LLM model.",
    strategyPlaceholder: 'Select cache strategy',
    strategies: {
      auto: 'Auto (Recommended)',
      gemini_cache: 'Gemini API Cache',
      openai_thread: 'OpenAI Thread Context',
      anthropic_cache: 'Anthropic Context Cache',
      context_injection: 'Context Injection',
      none: 'No Cache',
    },
    maxCacheTip:
      'Maximum size of document content to cache, in characters. Larger documents are truncated.',
    maxCachePlaceholder: 'Select max cache size',
    cacheSizes: {
      s50k: '50K characters (Small)',
      s200k: '200K characters (Medium)',
      s500k: '500K characters (Large)',
      s1m: '1M characters (Extra Large)',
      s2m: '2M characters (Maximum)',
    },
    batchTip:
      'Number of chunks sent to the LLM in one contextualisation call. Smaller batches are more reliable but slower; larger batches are faster but may hit rate or token limits.',
    batches: {
      b5: '5 chunks (Safest)',
      b10: '10 chunks (Conservative)',
      b20: '20 chunks (Recommended)',
      b30: '30 chunks (Fast)',
      b50: '50 chunks (Fastest)',
    },
    confluencePlaceholder: 'Select a model for contextualising page chunks',
  },
  notes: {
    jiraTitle: 'Jira knowledge base',
    jiraBody:
      'Ingests Jira tickets (JSON from the Atlassian connector). Each ticket becomes a single document. Only title + summary + description is embedded; all fields (status, type, priority, assignee, reporter, project, labels, components, versions, dates) are stored as filterable properties. Comments are searchable but not embedded.',
    jiraFallback:
      'Non-ticket files in this knowledge base fall back to General chunking.',
    confluenceTitle: 'Confluence knowledge base',
    confluenceBody:
      'Ingests Confluence pages (from the Atlassian connector). Page bodies are chunked with contextualised document understanding. Space, author and date fields are stored as filterable properties for the Confluence agent components.',
    confluenceFallback:
      'Attachments are ingested using the selected attachment chunk method.',
    categoricalTitle: 'Categorical data knowledge base',
    categoricalBody:
      'Upload files containing categorical values (for example homeroom names, school names, grade levels) for category resolution by the Retrieval component.',
    categoricalFormats: 'Supported formats:',
    categoricalTxt: 'Pipe-delimited, one per line:',
    categoricalCsv: 'Three columns:',
    categoricalXlsx:
      'Three columns: CategoryValue, CategoryColumn, CategoryTable',
    categoricalJson: 'Array of objects:',
    categoricalFooter:
      "Each unique value becomes a chunk for keyword-based lookup via the Retrieval component's Categorical KB option.",
  },
  tuning: {
    title: 'Connector import tuning',
    scope: 'Jira and Confluence connector imports only',
    hint: 'Batching and pod sizing for windowed connector imports. Uploaded files never use these settings.',
    speed: 'Ingestion speed',
    speedTip:
      'Controls how documents are batched across the fetch, contextualise and embed stages during connector import.',
    presets: {
      default: 'Inherit platform default',
      economy: 'Economy (cheapest, slowest)',
      balanced: 'Balanced',
      turbo: 'Turbo (fills the concurrency ceiling)',
      custom: 'Custom',
    },
    advanced: 'Advanced',
    fetchWindow: 'Fetch window',
    fetchWindowTip: 'Number of documents fetched from the source per shard.',
    contextualiseBatch: 'Contextualise batch',
    contextualiseBatchTip:
      'Number of documents sent to the contextualise stage per shard.',
    embedBatch: 'Embed batch',
    embedBatchTip: 'Number of documents sent to the embed stage per shard.',
    maxCtxShards: 'Max contextualise shards',
    maxCtxShardsTip:
      'Optional downward cap on the number of contextualise shards. Leave blank to use the default.',
    parseMaxMb: 'Max attachment size to parse (MB)',
    parseMaxMbExtra:
      'Attachments larger than this are skipped instead of parsed, and are not retried. Applies to attachments only — an issue or page carries no size of its own. 0 disables the limit.',
    inherit: 'Inherit platform default',
    contextualiseModel: 'Contextualise model',
    contextualiseModelExtra:
      'Blank = use the tenant chat model. Pick a cheaper or faster model to speed up and cut cost.',
  },
  save: {
    changes: '{{count}} changed:',
    noChanges: 'No unsaved changes.',
    on: 'on',
    off: 'off',
    empty: '—',
    selected: '{{count}} selected',
    photoChanged: 'new photo',
    newDocumentsShort: 'new documents only',
    newDocumentsOnly:
      'New documents only. Existing documents keep their method, even on re-parse.',
    save: 'Save',
    saveNoReparse: 'Save (no re-parse)',
    saveReparse:
      'Save & re-parse {{docs}} documents — deletes {{chunks}} chunks',
    reparseNote:
      '{{count}} of these need a re-parse to reach the {{chunks}} existing chunks. New uploads use them either way.',
    saveNote:
      'Save writes the settings and copies them onto every document. Existing chunks are not re-processed.',
    reparseConfirmTitle: 'Save and re-parse?',
    reparseConfirm:
      'This saves the settings, then deletes all {{chunks}} existing chunks and parses {{docs}} documents again from the start.',
    reparseStarted: 'Re-parse started for {{count}} documents.',
    reparseFailed: '{{count}} batches of documents failed to start parsing.',
    idsFailed: 'Could not list the documents to re-parse.',
  },
};
